/**
 * useRfiScenario — central hook for the Admin RFI Override system.
 *
 * SSOT contract:
 *   - This hook owns the in-memory `overrides` map (date → adjusted_kg).
 *   - Consumers inject `applyOverride(row)` into the `daily_analysis_rows`
 *     pipeline so every downstream RFI / waste / KPI / chart / narrative
 *     re-reads from the same `recommended_feed_kg` field. NO duplicate RFI
 *     logic anywhere else.
 *   - The original imported `recommended_feed_kg` is preserved in
 *     `row.original_recommended_feed_kg` for graph overlays + PDF appendix.
 *
 * Persistence:
 *   - Session-only sandbox by default (overrides live in component state).
 *   - Save / Load / Clone / Publish hit `/api/rfi-scenarios` (admin only).
 *   - Published scenarios are read-only — the hook surfaces `isLocked`.
 *   - Immutable audit log lives in `/api/rfi-scenarios/{id}/audit` on the
 *     server (write happens every PATCH).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => {
  const t = localStorage.getItem('access_token') || localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function useRfiScenario({ recordId, isAdmin }) {
  // ────── in-memory state ──────
  // overrides: { 'YYYY-MM-DD': { adjusted_recommended_kg, original_recommended_kg } }
  const [overrides, setOverrides] = useState({});
  const [scenarioId, setScenarioId] = useState(null);
  const [scenarioName, setScenarioName] = useState('');
  const [scenarioStatus, setScenarioStatus] = useState('draft'); // draft | published | archived
  const [savedScenarios, setSavedScenarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [enabled, setEnabled] = useState(false); // master toggle — when off, original values are used
  const [showOverlay, setShowOverlay] = useState(true); // chart overlay toggle
  // Keep the "true original" we saw the first time each date was overridden,
  // so the audit log reports the correct pre-edit value even after several edits.
  const originalsRef = useRef({});

  const isLocked = scenarioStatus === 'published' || scenarioStatus === 'archived';

  // ────── pure helpers ──────
  const isOverridden = useCallback((dateStr) => Object.prototype.hasOwnProperty.call(overrides, dateStr), [overrides]);
  const overrideCount = useMemo(() => Object.keys(overrides).length, [overrides]);

  /**
   * Inject the active override into a single daily-analysis row.
   * The original is preserved on the row so the chart-overlay + PDF appendix
   * can show originals vs adjusted side-by-side.
   */
  const applyOverride = useCallback((row) => {
    if (!enabled || !row || !row.date) return row;
    const ov = overrides[row.date];
    if (!ov) return row;
    const adjusted = Number(ov.adjusted_recommended_kg);
    if (!Number.isFinite(adjusted) || adjusted <= 0) return row;
    return {
      ...row,
      original_recommended_feed_kg: row.recommended_feed_kg,
      recommended_feed_kg: adjusted,
      is_rfi_override: true,
    };
  }, [enabled, overrides]);

  // ────── mutations ──────
  const setOverride = useCallback((dateStr, adjustedKg, originalKg) => {
    if (isLocked) {
      toast.error('Scenario is published / archived — clone it to edit.');
      return;
    }
    const adj = Number(adjustedKg);
    if (!Number.isFinite(adj) || adj <= 0) {
      toast.error(`Adjusted recommended feed must be > 0 (date ${dateStr}).`);
      return;
    }
    setOverrides((prev) => {
      const next = { ...prev };
      // First-edit guard: lock down the "true original" we observed.
      if (!originalsRef.current[dateStr] && originalKg != null) {
        originalsRef.current[dateStr] = originalKg;
      }
      next[dateStr] = {
        adjusted_recommended_kg: adj,
        original_recommended_kg: originalsRef.current[dateStr] ?? originalKg ?? null,
      };
      return next;
    });
    // Auto-enable scenario on first edit.
    setEnabled(true);
  }, [isLocked]);

  const resetRow = useCallback((dateStr) => {
    if (isLocked) {
      toast.error('Scenario is published / archived — clone it to edit.');
      return;
    }
    setOverrides((prev) => {
      const next = { ...prev };
      delete next[dateStr];
      return next;
    });
  }, [isLocked]);

  const resetAll = useCallback(() => {
    if (isLocked) {
      toast.error('Scenario is published / archived — clone it to edit.');
      return;
    }
    setOverrides({});
    originalsRef.current = {};
    setEnabled(false);
  }, [isLocked]);

  /**
   * Apply a percentage or kg/day delta to every row from `fromDate` onward
   * that does NOT yet have a manual override. Use `mode='percent'` for
   * +/- % adjustment, `mode='delta'` for kg/day add/subtract.
   */
  const applyToRemaining = useCallback((fromDate, rows, value, mode = 'percent') => {
    if (isLocked) {
      toast.error('Scenario is published / archived — clone it to edit.');
      return 0;
    }
    if (!rows || !rows.length) return 0;
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return 0;
    let touched = 0;
    setOverrides((prev) => {
      const next = { ...prev };
      for (const r of rows) {
        if (!r.date || r.date < fromDate) continue;
        const base = Number(r.original_recommended_feed_kg ?? r.recommended_feed_kg);
        if (!Number.isFinite(base) || base <= 0) continue;
        const adj = mode === 'delta' ? base + numeric : base * (1 + numeric / 100);
        if (!Number.isFinite(adj) || adj <= 0) continue;
        if (!originalsRef.current[r.date]) originalsRef.current[r.date] = base;
        next[r.date] = {
          adjusted_recommended_kg: Number(adj.toFixed(3)),
          original_recommended_kg: base,
        };
        touched++;
      }
      return next;
    });
    setEnabled(true);
    return touched;
  }, [isLocked]);

  // ────── server I/O ──────
  const refreshScenarioList = useCallback(async () => {
    if (!isAdmin || !recordId) return;
    try {
      const res = await axios.get(`${API}/api/rfi-scenarios`, {
        params: { record_id: recordId, include_archived: false },
        headers: authHeaders(),
      });
      setSavedScenarios(res.data || []);
    } catch (e) {
      console.error('refreshScenarioList', e);
    }
  }, [isAdmin, recordId]);

  const _overridePayload = useCallback(() => {
    const arr = [];
    const audit_meta = {};
    Object.entries(overrides).forEach(([date, v]) => {
      arr.push({
        date,
        original_recommended_kg: v.original_recommended_kg,
        adjusted_recommended_kg: v.adjusted_recommended_kg,
      });
      audit_meta[date] = { original_recommended_kg: v.original_recommended_kg };
    });
    return { arr, audit_meta };
  }, [overrides]);

  const saveScenario = useCallback(async (name) => {
    if (!recordId) {
      toast.error('No record loaded.');
      return null;
    }
    setLoading(true);
    try {
      const { arr } = _overridePayload();
      if (scenarioId) {
        const res = await axios.patch(`${API}/api/rfi-scenarios/${scenarioId}`, {
          name,
          overrides: arr,
          audit_meta: _overridePayload().audit_meta,
        }, { headers: { ...authHeaders(), 'Content-Type': 'application/json' } });
        setScenarioName(res.data.name);
        toast.success(`Saved "${res.data.name}" (${arr.length} overrides)`);
        await refreshScenarioList();
        return res.data;
      }
      const res = await axios.post(`${API}/api/rfi-scenarios`, {
        record_id: recordId,
        name: name || 'Untitled scenario',
        overrides: arr,
      }, { headers: { ...authHeaders(), 'Content-Type': 'application/json' } });
      setScenarioId(res.data.id);
      setScenarioName(res.data.name);
      setScenarioStatus(res.data.status);
      toast.success(`Saved scenario "${res.data.name}"`);
      await refreshScenarioList();
      return res.data;
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`);
      return null;
    } finally {
      setLoading(false);
    }
  }, [recordId, scenarioId, _overridePayload, refreshScenarioList]);

  const loadScenario = useCallback(async (id) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/rfi-scenarios/${id}`, { headers: authHeaders() });
      const next = {};
      const origs = {};
      (res.data.overrides || []).forEach((o) => {
        next[o.date] = {
          adjusted_recommended_kg: o.adjusted_recommended_kg,
          original_recommended_kg: o.original_recommended_kg,
        };
        if (o.original_recommended_kg != null) origs[o.date] = o.original_recommended_kg;
      });
      originalsRef.current = origs;
      setOverrides(next);
      setScenarioId(res.data.id);
      setScenarioName(res.data.name);
      setScenarioStatus(res.data.status);
      setEnabled(true);
      toast.success(`Loaded "${res.data.name}" (${res.data.status})`);
      return res.data;
    } catch (e) {
      toast.error(`Load failed: ${e?.response?.data?.detail || e.message}`);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const cloneScenario = useCallback(async (id) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/rfi-scenarios/${id}/clone`, {}, { headers: authHeaders() });
      toast.success(`Cloned as new draft "${res.data.name}"`);
      await refreshScenarioList();
      await loadScenario(res.data.id);
      return res.data;
    } catch (e) {
      toast.error(`Clone failed: ${e?.response?.data?.detail || e.message}`);
      return null;
    } finally {
      setLoading(false);
    }
  }, [loadScenario, refreshScenarioList]);

  const publishScenario = useCallback(async () => {
    if (!scenarioId) {
      toast.error('Save the scenario before publishing.');
      return null;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/rfi-scenarios/${scenarioId}/publish`, {}, { headers: authHeaders() });
      setScenarioStatus(res.data.status);
      toast.success(`Published — scenario is now locked from editing.`);
      await refreshScenarioList();
      return res.data;
    } catch (e) {
      toast.error(`Publish failed: ${e?.response?.data?.detail || e.message}`);
      return null;
    } finally {
      setLoading(false);
    }
  }, [scenarioId, refreshScenarioList]);

  const archiveScenario = useCallback(async () => {
    if (!scenarioId) return null;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/rfi-scenarios/${scenarioId}/archive`, {}, { headers: authHeaders() });
      setScenarioStatus(res.data.status);
      toast.success('Scenario archived.');
      await refreshScenarioList();
      return res.data;
    } catch (e) {
      toast.error(`Archive failed: ${e?.response?.data?.detail || e.message}`);
      return null;
    } finally {
      setLoading(false);
    }
  }, [scenarioId, refreshScenarioList]);

  const newScenario = useCallback(() => {
    setOverrides({});
    originalsRef.current = {};
    setScenarioId(null);
    setScenarioName('');
    setScenarioStatus('draft');
    setEnabled(false);
    // Mark this record as "user explicitly cleared" so the auto-load effect
    // below does not re-load on next render.
    if (recordId) dismissedAutoloadRef.current.add(recordId);
  }, [recordId]);

  // Auto-load the most recently updated scenario for the current record
  // whenever a new record is opened. Saves users from having to click Load
  // every time they reload the report. Skips:
  //   • when no admin user
  //   • when no recordId (record not yet loaded)
  //   • when the user already explicitly cleared via `newScenario` for this
  //     record in the current session
  //   • when a scenario is already loaded for this record
  // Re-enables overrides automatically since the scenario is the SSOT.
  const dismissedAutoloadRef = useRef(new Set());
  const autoloadedRecordRef = useRef(null);
  useEffect(() => {
    if (!isAdmin || !recordId) return;
    if (autoloadedRecordRef.current === recordId) return;
    if (dismissedAutoloadRef.current.has(recordId)) return;
    if (scenarioId) return; // already loaded
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/rfi-scenarios`, {
          params: { record_id: recordId, include_archived: false },
          headers: authHeaders(),
        });
        if (cancelled) return;
        const list = Array.isArray(res.data) ? res.data : [];
        setSavedScenarios(list);
        if (list.length === 0) {
          autoloadedRecordRef.current = recordId;
          return;
        }
        // Pick the most recently updated scenario (server already sorts desc).
        const mostRecent = list[0];
        const detail = await axios.get(`${API}/api/rfi-scenarios/${mostRecent.id}`, {
          headers: authHeaders(),
        });
        if (cancelled) return;
        const next = {};
        const origs = {};
        (detail.data.overrides || []).forEach((o) => {
          next[o.date] = {
            adjusted_recommended_kg: o.adjusted_recommended_kg,
            original_recommended_kg: o.original_recommended_kg,
          };
          if (o.original_recommended_kg != null) origs[o.date] = o.original_recommended_kg;
        });
        originalsRef.current = origs;
        setOverrides(next);
        setScenarioId(detail.data.id);
        setScenarioName(detail.data.name);
        setScenarioStatus(detail.data.status);
        setEnabled(true);
        autoloadedRecordRef.current = recordId;
        if (Object.keys(next).length > 0) {
          toast.message(`Loaded saved scenario "${detail.data.name}" (${detail.data.status}) · ${Object.keys(next).length} day${Object.keys(next).length === 1 ? '' : 's'} adjusted`);
        }
      } catch (e) {
        // Auto-load is best-effort — never block the page on a failure.
        console.warn('RFI scenario auto-load failed', e);
        autoloadedRecordRef.current = recordId;
      }
    })();
    return () => { cancelled = true; };
  }, [isAdmin, recordId, scenarioId]);

  const fetchAudit = useCallback(async () => {
    if (!scenarioId) return [];
    try {
      const res = await axios.get(`${API}/api/rfi-scenarios/${scenarioId}/audit`, { headers: authHeaders() });
      return res.data || [];
    } catch (e) {
      console.error('fetchAudit', e);
      return [];
    }
  }, [scenarioId]);

  return {
    // state
    overrides,
    overrideCount,
    isOverridden,
    enabled, setEnabled,
    showOverlay, setShowOverlay,
    scenarioId,
    scenarioName, setScenarioName,
    scenarioStatus,
    isLocked,
    savedScenarios,
    loading,
    // pure SSOT injector
    applyOverride,
    // edits
    setOverride,
    resetRow,
    resetAll,
    applyToRemaining,
    // server I/O
    refreshScenarioList,
    saveScenario,
    loadScenario,
    cloneScenario,
    publishScenario,
    archiveScenario,
    newScenario,
    fetchAudit,
  };
}
